import { useRef } from 'react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

/**
 * FadeInView 组件属性
 */
interface FadeInViewProps {
  /** 子元素内容 */
  children: React.ReactNode;
  /** 延迟时间（毫秒），用于错开多个元素的动画 */
  delay?: number;
  /** 自定义类名 */
  className?: string;
}

/**
 * FadeInView 动画组件
 * 
 * 当元素进入视口时播放淡入动画效果。
 * 使用 Intersection Observer API 检测元素可见性，
 * 通过 CSS transition 实现流畅的淡入效果。
 * 
 * @example
 * ```tsx
 * // 基础用法
 * <FadeInView>
 *   <div>这段内容会在进入视口时淡入</div>
 * </FadeInView>
 * 
 * // 带延迟的用法（适合列表项）
 * {items.map((item, index) => (
 *   <FadeInView key={item.id} delay={index * 100}>
 *     <Card>{item.content}</Card>
 *   </FadeInView>
 * ))}
 * ```
 */
export function FadeInView({ children, delay = 0, className = '' }: FadeInViewProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const isVisible = useIntersectionObserver(ref as React.RefObject<Element>, { threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}
